import { useEffect, useRef, useState } from 'react'

type ConditionGetter = () => boolean

export function useDeferredCallbackWhenReady<Args extends any[]>(
  conditions: ConditionGetter[],
  callback: (...args: Args) => Promise<void>
) {
  const [executionRequested, setExecutionRequested] = useState(false)
  const pendingArgsRef = useRef<Args | null>(null)
  const isExecutingRef = useRef(false)
  const [isWaiting, setIsWaiting] = useState(false)

  useEffect(() => {
    if (!executionRequested || isExecutingRef.current) return

    const allReady = conditions.every(getCondition => getCondition())

    if (allReady && pendingArgsRef.current) {
      isExecutingRef.current = true
      setIsWaiting(false)

      callback(...pendingArgsRef.current)
        .catch(e => {
          console.error('Deferred callback error:', e)
        })
        .finally(() => {
          pendingArgsRef.current = null
          setExecutionRequested(false)
          isExecutingRef.current = false
        })
    }
  }, [executionRequested, conditions, callback])

  const trigger = (...args: Args) => {
    pendingArgsRef.current = args
    setExecutionRequested(true)
    setIsWaiting(true)
  }

  return {
    trigger,
    isExecuting: executionRequested || isExecutingRef.current,
    isWaiting,
  }
}
